import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Dialog,
  Button,
  Select,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  DialogTitle,
  FormControl,
  DialogContent,
  FormHelperText,
  CircularProgress,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { I18nContext } from 'translations';
import { useStateValue } from 'context/store';
import {
  cancelBookingAction,
  getCancelationReasonsAction,
} from 'context/booking/actions';
import Loading from 'components/Loading';

const OTHER_REASON_ID = 39;

function ModalCancelBooking({ open, closeModal, bookingId, token }) {
  const { translate } = useContext(I18nContext);
  const [, dispatch] = useStateValue();
  const [reasons, setReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reasonId, setReasonId] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      setReasonId('');
      setOtherReason('');
      setErrors({});
      return;
    }
    let active = true;
    setLoadingReasons(true);
    getCancelationReasonsAction(dispatch).then((data) => {
      if (!active) return;
      setReasons(data);
      setLoadingReasons(false);
    });
    return () => {
      active = false;
    };
  }, [open, dispatch]);

  const isOther = reasonId === OTHER_REASON_ID;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reasonId) {
      setErrors({
        reasonId: translate('app.booking.cancelation.reasonRequired'),
      });
      return;
    }
    setErrors({});

    const trimmedOther = isOther ? otherReason.trim() : '';
    setSubmitting(true);
    await cancelBookingAction(bookingId, token, dispatch, {
      cancelation_reason_cd: reasonId,
      other_cancelation_reason: trimmedOther || undefined,
    });
    setSubmitting(false);
    closeModal();
  };

  const confirmDisabled = submitting || loadingReasons || !reasonId;

  return (
    <Dialog
      open={open}
      aria-describedby="cancel-booking-dialog"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle disableTypography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h6"
            style={{ color: '#7C3AED', fontWeight: 'bold' }}
          >
            {translate('app.booking.cancelation.title')}
          </Typography>
          <IconButton
            onClick={closeModal}
            size="small"
            disabled={submitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent style={{ padding: '10px 40px 40px' }}>
        {submitting && <Loading />}
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                style={{
                  color: '#7C3AED',
                  fontWeight: 'bold',
                  marginBottom: 10,
                }}
              >
                {translate('app.booking.cancelation.reasonLabel')}
              </Typography>
              <FormControl
                variant="outlined"
                fullWidth
                size="small"
                error={!!errors.reasonId}
              >
                <Select
                  value={reasonId}
                  onChange={(e) => setReasonId(e.target.value)}
                  displayEmpty
                  disabled={loadingReasons || submitting}
                >
                  <MenuItem value="" disabled>
                    {loadingReasons ? (
                      <Box display="flex" alignItems="center">
                        <CircularProgress size={14} style={{ marginRight: 8 }} />
                        {translate(
                          'app.booking.cancelation.reasonPlaceholder'
                        )}
                      </Box>
                    ) : (
                      translate('app.booking.cancelation.reasonPlaceholder')
                    )}
                  </MenuItem>
                  {reasons.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.reasonId && (
                  <FormHelperText>{errors.reasonId}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {isOther && (
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  style={{
                    color: '#7C3AED',
                    fontWeight: 'bold',
                    marginBottom: 10,
                  }}
                >
                  {translate('app.booking.cancelation.otherLabel')}
                </Typography>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder={translate(
                    'app.booking.cancelation.otherPlaceholder'
                  )}
                  fullWidth
                  multiline
                  rows={3}
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  inputProps={{ maxLength: 500 }}
                  disabled={submitting}
                />
              </Grid>
            )}

            <Grid item xs={12} className="text-right">
              <Button
                variant="outlined"
                onClick={closeModal}
                disabled={submitting}
                style={{
                  borderRadius: '25px',
                  textTransform: 'none',
                  padding: '8px 30px',
                  marginRight: 10,
                }}
              >
                {translate('cancel')}
              </Button>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={confirmDisabled}
                startIcon={
                  submitting && (
                    <CircularProgress size={16} color="inherit" />
                  )
                }
                style={{
                  borderRadius: '25px',
                  textTransform: 'none',
                  padding: '8px 30px',
                }}
              >
                {translate('app.booking.cancelation.confirm')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ModalCancelBooking;
